import { Link } from "react-router-dom";
import {
  LifeBuoy, Mail, UserRound, Building2, Radar, Send, MessagesSquare, Tag,
  ArrowRight, HelpCircle,
} from "lucide-react";

const SUPPORT_EMAIL = "support@jobbasket.in";

const STEPS = [
  {
    icon: UserRound, title: "Complete your profile",
    text: "Add your details and upload a resume — it's attached to every application automatically.",
    to: "/app/profile", cta: "Open Profile",
  },
  {
    icon: Building2, title: "Save companies you like",
    text: "Saving a company uses one company credit and unlocks its openings for you.",
    to: "/app/companies", cta: "Browse Companies",
  },
  {
    icon: Radar, title: "Apply from Discover & Apply",
    text: "Pick a job, choose a template, and send — each application uses one application credit.",
    to: "/app/discover", cta: "Discover Jobs",
  },
  {
    icon: Send, title: "Track every application",
    text: "See delivery, open, and reply status for each application in one place.",
    to: "/app/applications", cta: "View Applications",
  },
  {
    icon: MessagesSquare, title: "Reply from Communications",
    text: "Answer recruiters with ready-made templates. Each reply uses one reply credit.",
    to: "/app/communications", cta: "Open Communications",
  },
];

const FAQS = [
  {
    q: "What are the different credits?",
    a: "Application credits send job applications, reply credits send replies to recruiters, and company credits let you save companies. Your plan includes a set of each — check Plans & Pricing for your balance.",
  },
  {
    q: "Do unused credits roll over?",
    a: "Free monthly replies refresh every 30 days. Purchased top-up credits and company slots never expire. Company credits are lifetime — removing a saved company does not refund its credit.",
  },
  {
    q: "Can recruiters see my email address?",
    a: "Recruiters see your name and application email. Their replies arrive in your Communications inbox — their address is never shown to you, and yours is handled by our secure mailing system.",
  },
  {
    q: "How do I upgrade or change my plan?",
    a: "Open Plans & Pricing from the sidebar to compare plans, upgrade instantly, or buy top-up credits without changing plans.",
  },
];

export default function HelpSupport() {
  return (
    <div data-testid="help-page" className="max-w-4xl space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display flex items-center gap-2.5">
          <LifeBuoy className="w-7 h-7 text-amber-500" /> Help &amp; Support
        </h1>
        <p className="text-slate-500 mt-1.5">Quick answers and a direct line to our team.</p>
      </div>

      {/* Contact card */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-100/60 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-slate-900">Talk to our team</h2>
          <p className="text-sm text-slate-500 mt-0.5">Questions about your account, plan, or payments? We usually reply within one business day.</p>
        </div>
        <a data-testid="help-contact-email" href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-500 text-black font-semibold px-5 py-2.5 text-sm hover:bg-amber-400 transition-colors shrink-0">
          <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
        </a>
      </div>

      {/* Getting started */}
      <div>
        <h2 className="font-bold text-slate-900 text-lg">Getting started</h2>
        <div className="mt-4 space-y-3">
          {STEPS.map((s, i) => (
            <Link key={s.to} to={s.to} data-testid={`help-step-${i + 1}`}
              className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-amber-300 transition-colors">
              <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <s.icon className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.text}</p>
              </div>
              <span className="text-xs font-semibold text-amber-700 inline-flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {s.cta} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="font-bold text-slate-900 text-lg">Common questions</h2>
        <div className="mt-4 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`help-faq-${i + 1}`} className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <p className="text-sm font-semibold text-slate-900 flex items-start gap-2">
                <HelpCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> {f.q}
              </p>
              <p className="text-sm text-slate-500 mt-1.5 pl-6">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Manage your plan anytime from <Link to="/app/pricing" className="text-amber-700 font-medium hover:underline">Plans &amp; Pricing</Link>.
      </p>
    </div>
  );
}
